import React, { useState } from 'react'
import { Box, Container, Tabs, Tab, CircularProgress, Fade, Typography, Alert, useTheme } from '@mui/material'
import InsightsIcon from '@mui/icons-material/Insights'
import HubIcon from '@mui/icons-material/Hub'
import ViewStreamIcon from '@mui/icons-material/ViewStream'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import useRouter from '../hooks/useRouter'
import useProgrammeTracker from '../hooks/useProgrammeTracker'
import TrackerHeader from '../components/programme-tracker/TrackerHeader'
import SummaryBar from '../components/programme-tracker/SummaryBar'
import SectionView from '../components/programme-tracker/SectionView'
import ReferenceView from '../components/programme-tracker/ReferenceView'
import { usePrefersReducedMotion } from '../components/programme-tracker/motion'
import { TRACKER_MAX_WIDTH } from '../components/programme-tracker/atoms'

// Section tabs take the icons the first two streams had before sections were
// generic; any further section gets a neutral one.
const SECTION_ICONS = [InsightsIcon, HubIcon]

const ProgrammeTracker: React.FC = () => {
  const theme = useTheme()
  const router = useRouter()
  const slug = router.params.slug || ''
  const [tab, setTab] = useState(0)
  const reduced = usePrefersReducedMotion()

  const { tracker, isLoading, isError, error } = useProgrammeTracker(slug)

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#0072C6' }} />
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: theme.palette.text.secondary, letterSpacing: 0.3 }}>
          Loading programme tracker…
        </Typography>
      </Box>
    )
  }

  if (isError || !tracker) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">{error || 'Programme tracker not found.'}</Alert>
      </Container>
    )
  }

  // A section deleted while the page is open must not leave the tab out of range.
  const safeTab = Math.min(tab, tracker.sections.length)
  const activeSection = safeTab > 0 ? tracker.sections[safeTab - 1] : undefined

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100%' }}>
      <TrackerHeader tracker={tracker} />

      <Box sx={{ maxWidth: TRACKER_MAX_WIDTH, mx: 'auto', width: '100%' }}>
        <SummaryBar tracker={tracker} />

        <Box sx={{ px: { xs: 2, md: 4 } }}>
          <Tabs
            value={safeTab}
            onChange={(_, v) => setTab(v)}
            sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
          >
            <Tab icon={<MenuBookIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Reference" sx={{ minHeight: 52, textTransform: 'none', fontWeight: 600 }} />
            {tracker.sections.map((section, i) => {
              const SectionIcon = SECTION_ICONS[i] ?? ViewStreamIcon
              return (
                <Tab
                  key={section.id}
                  icon={<SectionIcon sx={{ fontSize: 18 }} />}
                  iconPosition="start"
                  label={section.name}
                  sx={{ minHeight: 52, textTransform: 'none', fontWeight: 600 }}
                />
              )
            })}
          </Tabs>
        </Box>

        <Box sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
          <Fade in key={safeTab} timeout={reduced ? 0 : 320}>
            <Box>
              {safeTab === 0 && <ReferenceView tracker={tracker} />}
              {activeSection && <SectionView section={activeSection} branding={tracker.branding} />}
            </Box>
          </Fade>
        </Box>
      </Box>
    </Box>
  )
}

export default ProgrammeTracker
